import React from 'react';
import { ContentLayout } from './ContentLayout';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import {
    TestimonialSection,
    FAQSection,
    EnhancedCTASection,
    StepByStepSection,
    Testimonial,
    FAQ,
    Step
} from './SharedSections';
import { fadeInUp, staggerContainer, viewportConfig } from '../../utils/animationVariants';

interface UseCasePageProps {
    slug: string;
    title: string;
    subtitle: string;
    description: string;
    features: string[];
    image: string;
    steps?: Step[];
    testimonials?: Testimonial[];
    faqs?: FAQ[];
    onGetStarted: () => void;
}

export const UseCasePage: React.FC<UseCasePageProps> = ({
    title,
    subtitle,
    description,
    features,
    image,
    steps,
    testimonials,
    faqs,
    onGetStarted
}) => {
    return (
        <ContentLayout title={title} description={description} onGetStarted={onGetStarted}>
            <div className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-20">
                <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                    >
                        <div className="inline-block px-4 py-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-medium mb-6">
                            NoteSpace for {subtitle}
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight leading-tight">
                            {title}
                        </h1>
                        <p className="text-xl text-slate-600 dark:text-slate-400 mb-10 leading-relaxed">
                            {description}
                        </p>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={viewportConfig}
                            variants={staggerContainer}
                            className="space-y-4 mb-12"
                        >
                            {features.map((feature, i) => (
                                <motion.div
                                    key={i}
                                    variants={fadeInUp}
                                    className="flex items-start gap-3 group"
                                >
                                    <CheckCircle2 className="w-7 h-7 text-green-500 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                                    <span className="text-slate-700 dark:text-slate-200 text-xl font-medium">{feature}</span>
                                </motion.div>
                            ))}
                        </motion.div>

                        <button
                            onClick={onGetStarted}
                            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-5 rounded-full font-bold text-xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-indigo-500/20 active:scale-95 hover:-translate-y-1"
                        >
                            Start Creating Now <ArrowRight className="w-6 h-6" />
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 rounded-[2rem] transform rotate-3 blur-2xl" />
                        <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 p-2 bg-white dark:bg-slate-900">
                            <img
                                src={image}
                                alt={title}
                                className="rounded-xl w-full object-cover"
                            />
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Dynamic Sections */}
            {steps && <StepByStepSection title="Your Workflow" steps={steps} />}

            {testimonials && testimonials.length > 0 && (
                <TestimonialSection testimonials={testimonials} />
            )}

            {faqs && faqs.length > 0 && (
                <FAQSection faqs={faqs} />
            )}

            {/* Final CTA */}
            <EnhancedCTASection onGetStarted={onGetStarted} />
        </ContentLayout>
    );
};
